<template>
  <div class="mb-8">
    <div v-if="Object.keys(errors).length">
      <div v-for="(errorKey, eIndx) in Object.keys(errors)" class="py-1 px-2 bg-red-200 border-red-300 text-red-800 mb-1 rounded-lg" :key="eIndx">
        <span>{{ errors[errorKey].join(' ') }}</span>
      </div>
    </div>
    <form @submit.prevent="() => onSubmit(form)" class="space-y-8 divide-y divide-gray-200">
      <div class="bg-white rounded shadow overflow-hidden px-3 py-2">
        <div class="space-y-8 divide-y divide-gray-200">
          <div>
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 md:grid-cols-12">
              <div class="sm:col-span-4">
                <TextInput label="Offer Title" v-model="form.title" />
              </div>
              <div>
                <SelectInput label="Type" v-model="form.type" @input="onGlobalSelected">
                  <option :value="null">--</option>
                  <option value="global">Global</option>
                </SelectInput>
              </div>

              <div class="sm:col-span-2">
                <DateInput label="Start Date" v-model="form.start_date" />
              </div>
              <div class="sm:col-span-2">
                <DateInput label="End Date" v-model="form.end_date" />
              </div>

              <div class="sm:col-span-2">
                <SelectInput label="Price Group" v-model="form.price_group">
                  <option v-for="option in pricingOptions" :value="option.key">{{option.label}}</option>
                </SelectInput>
              </div>

              <div class="sm:col-span-1">
                <SelectInput label="Priority #" v-model.number="form.priority">
                  <option :value="100">auto</option>
                  <option v-for="(num, i) in 99":value="num" :key="num">{{ num }}</option>
                </SelectInput>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div class="py-2">
        <div class="relative flex">
          <div class="flex items-center">
            <input type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" v-model:checked="form.active">
          </div>
          <div class="ml-3 text-base">
            <label class="text-gray-700">Activate Offer</label>
          </div>
        </div>
      </div>

      <div class="py-2">
        <h3 class="text-lg leading-loose">Applicable Customers</h3>
        <p class="text-sm text-gray-400 leading-normal">Applies to specific customers or by their business type only if added, otherwise considered applicable to all customers.</p>
        <div class="bg-white rounded shadow px-3 py-2">
          <div class="space-y-8 divide-y divide-gray-200">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Type</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase ">Customer</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business Type</th>
                <th></th>
              </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
              <tr class="bg-gray-200">
                <td class="px-6 whitespace-nowrap text-sm font-medium text-gray-900">
                  <select-input v-model="customerApplicableForm.account_type" class="w-52">
                    <option value="business">Business</option>
                    <option value="personal">Personal</option>
                  </select-input>
                </td>
                <td class="px-6 whitespace-nowrap text-sm font-medium text-gray-900">
                  <select-input v-model="customerApplicableForm.location" class="w-52">
                    <option :value="null">any</option>
                    <option value="island">Island</option>
                    <option value="male">Male</option>
                    <option value="hulhumale">Hulhumale</option>
                  </select-input>
                </td>

                <td class="px-6 whitespace-nowrap text-sm font-medium text-gray-900">
                  <v-select id="shop"
                            v-model="customerApplicableForm.customer"
                            :options="selectableCustomerAccounts"
                            :filterable="false"
                            :reduce="option => { return option; }"
                            label="name"
                            class="tailwind-v-select w-96"
                            @search="searchCustomerList">
                    <template slot="option" slot-scope="option">
                      <div class="flex flex-col">
                        <p class="text-base text-gray-800">{{ option.name }}</p>
                        <div class="text-xs text-gray-500">{{ `${option.ls_number} | ${option.location}` }}</div>
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                          <span class="flex items-baseline gap-2">
                            <p class="text-base text-gray-800">{{ option.name }} | <small>{{ option.ls_number }}</small></p>
                          </span>
                    </template>
                    <template slot="no-options">
                      type to search customer...
                    </template>
                  </v-select>

                </td>
                <td class="px-6 whitespace-nowrap text-sm font-medium text-gray-900">
                  <select-input v-if="customerApplicableForm.account_type === 'business' && !customerApplicableForm.customer" v-model="customerApplicableForm.business_type_code" class="w-60">
                    <option :value="null">Any</option>
                    <option v-for="(btype, bkey) in businessTypes" :value="bkey" :key="bkey">{{ btype }}</option>
                  </select-input>
                  <div v-else>N/A</div>
                </td>
                <td class="text-right px-4">
                  <button class="rounded bg-primary-500 text-white p-2 disabled:opacity-50" :disabled="!customerApplicableForm.account_type" @click.prevent="addApplicableCustomer"><span class="text-lg">+</span> Add</button>
                </td>
              </tr>

              <tr v-for="(crow, cridx) in form.applicable_customers" :key="cridx" class="border-b border-gray-100">
                <td class="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <span v-if="crow.account_type === 'business'">Business</span>
                  <span v-else>Personal</span>
                </td>
                <td class="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <span v-if="!crow.location">Any</span>
                  <span v-else class="capitalize">{{ crow.location }}</span>
                </td>
                <td class="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div v-if="crow.customer">
                    {{ crow.customer.name }}
                    <div class="text-sm text-gray-400">{{ `${crow.customer.ls_number} | ${crow.customer.location}` }}</div>
                  </div>
                  <span v-else>Any</span>
                </td>
                <td class="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <span v-if="crow.business_type_code">{{ businessTypes[crow.business_type_code] }}</span>
                  <span v-else>Any</span>
                </td>

                <td class="text-right px-4 flex justify-end items-center py-2">
                  <button class="block rounded bg-red-500 text-white p-2 hover:red-900" @click.prevent="removeApplicableCustomer(cridx)"><Icon name="trash" class="h-4 w-4 fill-current"/></button>
                </td>
              </tr>
              </tbody>

            </table>
          </div>
        </div>
      </div>



      <div v-if="form.type !== 'global'" class="py-2">
        <h3 class="text-lg leading-loose">Products Applicable</h3>
        <p class="text-sm text-gray-400 leading-normal">Applies to specific products if added otherwise considered applicable to all products.</p>
        <div class="bg-white rounded shadow px-3 py-2">
          <div class="space-y-8 divide-y divide-gray-200">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Code</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apply Action</th>
                <th></th>
              </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
              <tr class="bg-gray-200">
                <td class="px-6 whitespace-nowrap text-sm font-medium text-gray-900">
                  <v-select id="shop"
                            v-model="applicableForm.item"
                            :options="selectableItemCodes"
                            :filterable="false"
                            :reduce="option => { return {item_code: option.item_code, desc: option.description }}"
                            @option:selected="reloadItemUnits"
                            label="description"
                            class="tailwind-v-select w-full"
                            @search="searchItemCodes">
                    <template slot="option" slot-scope="option">
                      <div class="flex flex-col">
                        <p class="text-base text-gray-800">{{ option.description }}</p>
                        <div class="text-xs text-gray-500">{{ option.item_code }}</div>
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                          <span class="flex items-baseline gap-2">
                            <p class="text-base text-gray-800">{{ option.description }}</p>
                            <div class="text-xs text-gray-500">{{ option.item_code }}</div>
                          </span>
                    </template>
                    <template slot="no-options">
                      type to search item codes...
                    </template>
                  </v-select>
                </td>
                <td class="px-6 whitespace-nowrap text-sm font-medium text-gray-900">
                  <select class="w-full px-4 h-8" v-model="applicableForm.unit">
                    <option v-for="unit in selectableItemUnits" :value="unit.key">{{ unit.label }}</option>
                  </select>
                </td>
                <td class="px-6 whitespace-nowrap text-sm font-medium text-gray-900">
                  <select class="w-full px-4 h-8" v-model="applicableForm.action">
                    <option v-for="action in form.actions" :key="action.id" :value="action.id">Action #{{ action.id }}</option>
                  </select>
                </td>
                <td class="text-right px-4">
                  <button class="rounded bg-primary-500 text-white p-2 disabled:opacity-50" :disabled="canAddApplicableItem === false" @click.prevent="addApplicableItemRow"><span class="text-lg">+</span> Add</button>
                </td>
              </tr>

              <tr v-for="(row, ridx) in form.applicables" :key="ridx" class="border-b border-gray-100">
                <td class="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ row.item.desc }}
                  <div class="text-sm text-gray-400">{{ row.item.item_code }}</div>
                  <div v-if="dups[row.item.item_code]" class="text-xs text-blue-400 mt-1">
                    <span>Exist in offers </span> <InertiaLink class="border-l border-gray-400 mx-1 px-1" v-for="(exOffer, xindx) in dups[row.item.item_code]" :key="xindx" :href="route('manage.offers.edit', exOffer)">{{ exOffer }}</InertiaLink>
                  </div>
                </td>
                <td class="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ row.unit }}</td>
                <td class="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <select class="w-full px-1 bg-transparent" v-model="row.action">
                    <option v-for="action in form.actions" :key="action.id" :value="action.id">Action #{{ action.id }}</option>
                  </select>
                </td>
                <td class="text-right px-4 flex justify-end items-center py-2">
                  <button class="block rounded bg-red-500 text-white p-2 hover:red-900" @click.prevent="removeApplicable(ridx)"><Icon name="trash" class="h-4 w-4 fill-current"/></button>
                </td>
              </tr>
              </tbody>

            </table>
          </div>
        </div>
      </div>

      <div class="py-2">
        <GlobalOfferAction
          v-if="form.type === 'global'"
          v-model="form.actions[0].action.global"
          :usages="offerUsages"
        />

        <Tabs v-else v-on:addtab="addOverrideActionTab" :showNewTab="true" v-slot="{selectedIndex}">
          <Tab v-for="(actionTab, tabIndx) in form.actions" :title="`Action #` + actionTab.id" :key="actionTab.id" :active="selectedIndex === tabIndx">
            <div class="flex justify-between p-2">
              <h2 class="text-lg font-bold">Action # {{ actionTab.id }}</h2>
              <a @click="removeOverrideTab(actionTab)" class="text-red-400 hover:text-red-600 cursor-pointer"><Icon name="trash" class="w-6 h-6 fill-current"/></a>
            </div>
            <OfferAction v-model="form.actions[tabIndx].action" :key="actionTab.id" />
          </Tab>
        </Tabs>

      </div>

      <div class="py-2">
        <div class="bg-white rounded p-4 flex justify-end gap-8">
          <div class="flex-grow flex items-start" v-if="mode === 'edit'">
            <button class="bg-red-500 text-white p-2" @click.prevent="deleteAction"><span class="text-lg">Delete</span></button>
          </div>
          <InertiaLink :href="route('manage.offers')" class="block flex rounded bg-gray-200 hover:bg-gray-50 text-gray-800 py-2 px-4">Cancel</InertiaLink>
          <button class="rounded bg-primary-500 hover:bg-primary-600 text-white py-2 px-8" @click.prevent="saveOffer">
            <span class="text-lg" v-if="mode === 'create'">Create Offer</span>
            <span class="text-lg" v-else>Save Offer</span>
          </button>
        </div>
      </div>
    </form>
  </div>

</template>

<script>
import LoadingButton from '@/Shared/LoadingButton'
import vSelect from 'vue-select'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import DateInput from '@/Shared/DateInput'
import Icon from '@/Shared/Icon'
import Tabs from '@/Shared/Tabs'
import Tab from '@/Shared/Tab'
import __ from 'lodash'
import OfferAction from './OfferAction'
import GlobalOfferAction from "./OfferActions/GlobalOfferAction.vue";

export default {
  name: 'Form',
  components: {
    GlobalOfferAction,
    OfferAction, LoadingButton, SelectInput, TextInput, DateInput, Icon, Tabs, Tab, vSelect
  },
  props: {
    errors: {
      type: Object,
    },
    mode: {
      type: String,
      default: 'create'
    },
    businessTypes: Object,
    formData : Object,
    onSubmit : Function,
    dups: [Object,Array],
  },

  data() {
    return {
      applicableForm: {},
      customerApplicableForm: {},
      selectableCustomerAccounts: [],
      selectableItemCodes: [],
      selectableItemUnits: [],
      form: {
        start_date: null,
        end_date: null,
        type: null,
        active: false,
        applicables: [],
        applicable_customers: [],
        actions: []
      },
      pricingOptions: [
        {key: "wholesale", label: "Wholesale"},
        {key: "retail", label: "Retail"},
      ]
    }
  },

  mounted() {
    if(this.formData) {
      this.form = Object.assign({
        active: false,
        applicables: [],
        applicable_customers: [],
        actions: []
      }, this.formData)
    } else {
      this.addOverrideActionTab()
    }
  },

  computed: {
    offerUsages() {
      if (this.formData && Object.keys(this.formData).length) {
        return this.formData.offer_usages
      }
      return {}
    },

    canAddApplicableItem() {
      if(this.applicableForm.item && this.applicableForm.unit && this.applicableForm.action) {
        return true;
      }

      return false;
    }
  },

  methods: {
    onGlobalSelected(val) {
      if (!this.formData) {
        const global = {
          enable: true,
          applicable_type: 'every_order',
          per_customer: false,
          amount_gt: null,
          rules: [],
        };

        global.enable = val === 'global';

        this.form.actions[0].action.global = global
      }
    },
    addApplicableItemRow() {
      this.form.applicables.push(Object.assign({}, this.applicableForm))
      this.resetApplicableForm()
    },

    addApplicableCustomer() {
      this.form.applicable_customers.push(Object.assign({
         customer: null,
         business_type_code: null,
         location: null,
      }, this.customerApplicableForm))
      this.customerApplicableForm = {};
    },

    removeApplicableCustomer(rowIndex) {
      if(this.form.applicable_customers[rowIndex]) {
        this.form.applicable_customers.splice(rowIndex, 1)
      }
    },

    removeApplicable(rowIndex) {
      if(this.form.applicables[rowIndex]) {
        this.form.applicables.splice(rowIndex, 1)
      }
    },

    resetApplicableForm() {
      this.applicableForm = {
        item: null,
        unit: null,
        action: null
      }
    },

    getUniqueActionID(max = 0) {
      let id = max + 1;
      // check if the id exist in the action
      while(this.form.actions.find(action => action.id === id)) {
        id++;
      }

      return id;
    },

    addOverrideActionTab() {
      // get unique id
      let id = this.getUniqueActionID();
      this.form.actions.push({
        id,
        action: {
          buyXgetYFree: {
            enable: false
          },
          itemDiscounts : {
            enable: false
          },
          global: {
            enable: false,
            applicable_type: 'every_order',
            per_customer: false,
            rules: [],
          }
        }
      })
    },

    removeOverrideTab(tab) {
      this.form.actions.splice(this.form.actions.indexOf(tab), 1);
    },

    reloadItemUnits() {
      let selectedOption = this.applicableForm.item.item_code;
      if(selectedOption) {
        this.$http.get(this.route('ajax.item_units', {item_code: selectedOption})).then(response => {
          this.selectableItemUnits = response.data
        })
      }
    },

    searchItemCodes(search, loading) {
      if(search) {
        loading(true)
        this.$http.get(this.route('ajax.items_list_search', {search})).then( response => {
          loading(false)
          this.selectableItemCodes = response.data;
        })
      }
      return [];
    },

    searchCustomerList(search, loading) {
      if(search) {
        loading(true)
        this.$http.get(this.route('ajax.applicable-customers-search', {search, account_type: this.customerApplicableForm.account_type})).then( response => {
          loading(false)
          this.selectableCustomerAccounts = response.data;
        })
      }
      return [];
    },

    saveOffer() {
      this.$emit('submit', this.form);
    },

    deleteAction() {
      this.$emit('delete', this.form);
    }
  }
}
</script>
